import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleDeliveryOrderOut` (camelCase aliases) — frmCtrlSalesQryDO datasheet. */
export interface SaleDeliveryOrder {
  id: number
  fromWarehouse: string | null
  saleContract: string | null
  buyersRef: string | null
  doNo: string | null
  doDate: string | null
  loadingDate: string | null
  accumulateDelivered: number | null
  pending: number | null
  nwKgs: number | null
  buyerName: string | null
  contractWeightKg: number | null
  quality: string | null
  certification: string | null
  deliveryTerms: string | null
  contractWarehouse: string | null
  shippingMonth: string | null
  shippingTo: string | null
  packaging: string | null
  icoNo: string | null
  marking: string | null
  dunnage: string | null
  typeOfInspection: string | null
  inspectionName: string | null
  fumiDose: string | null
  fumigationName: string | null
  noContTruckNum: number | null
  truckName: string | null
  customs: string | null
  customsName: string | null
  bookingNote: string | null
  bookingNo: string | null
  freightPayable: string | null
  blNumber: string | null
  shippingLinesName: string | null
  vesselNameVoyage: string | null
  exportTerminalName: string | null
  internalTransshipmentPort: string | null
  portOfLoadingCode: string | null
  portOfLoadingName: string | null
  transhipmentPortCode: string | null
  transhipmentPortName: string | null
  destinationPortCode: string | null
  destinationPortName: string | null
  placeOfDelivery: string | null
  cutOff: string | null
  etd: string | null
  eta: string | null
  atd: string | null
  docsPresentationDate: string | null
  sendingDocsTo: string | null
  indication: string | null
  docsReceiverFullname: string | null
  fullName: string | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface SaleDeliveryOrderListResponse {
  items: SaleDeliveryOrder[]
  total: number
  page: number
  pageSize: number
}

export interface SaleDeliveryOrderListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryDOAdd New/Edit/Copy payload — camelCase → delivery_order_scs. */
export interface SaleDeliveryOrderWrite {
  doNo?: string | null
  doDate?: string | null
  warehouseType?: string | null
  fromWarehouse?: string | null
  saleContract?: string | null
  loadingDate?: string | null
  netWeightKg?: number | null
  icoNo?: string | null
  marking?: string | null
  dunnage?: string | null
  typeOfInspection?: string | null
  inspectionCompany?: string | null
  fumiDose?: string | null
  fumigationCompany?: string | null
  noContTruck?: number | null
  truckCompany?: string | null
  bookingNote?: string | null
  bookingNo?: string | null
  freightPayable?: string | null
  blNumber?: string | null
  shippingLine?: string | null
  vesselName?: string | null
  voyage?: string | null
  exportTerminal?: string | null
  portOfLoading?: string | null
  transhipmentPort?: string | null
  destinationPort?: string | null
  placeOfDelivery?: string | null
  cutOff?: string | null
  etd?: string | null
  eta?: string | null
  atd?: string | null
  docsPresentationDate?: string | null
  documentReceiver?: string | null
  pic?: string | null
  customs?: string | null
  internalTransshipmentPort?: string | null
  /** Nguồn match khi DO tạo từ Matching P-S "Save vs DO". */
  matchType?: string | null
  matchId?: number | null
}

/** GET /{id}/form shape (codes + display labels) for Edit/Copy hydrate. */
export type SaleDeliveryOrderForm = Record<string, unknown> & { id: number }

/** frmCtrlSalesDispatchPrint subform row (cb<i> + quantity1<i> + quantity2<i>). */
export interface DispatchDocRow {
  docName: string | null
  numOriginal: number | null
  numCopies: number | null
}
export interface DispatchPrintForm {
  cpCode: string | null
  rows: DispatchDocRow[]
  docOptions: string[]
}

export interface DoLookupItem { value: string; label: string }
export interface DoLookupResponse { items: DoLookupItem[]; total: number }
export interface SuggestIndexResponse { index: number; exists: boolean }
export interface DoSnapshotResponse { contractTotal: number; deliveredTotal: number; todeDelivery: number }

const saleDeliveryOrderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleDeliveryOrders: builder.query<SaleDeliveryOrderListResponse, SaleDeliveryOrderListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-delivery-orders',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleDeliveryOrder'],
    }),

    getSaleDeliveryOrder: builder.query<SaleDeliveryOrder, number>({
      query: (id) => `/sale-delivery-orders/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleDeliveryOrder', id }],
    }),

    deleteSaleDeliveryOrder: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-delivery-orders/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),

    /** Email-permissioned hard-delete access (legacy btnDelete rule). */
    getSaleDeliveryOrderCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-delivery-orders/access/can-delete',
    }),

    // ── frmCtrlSalesQryDOAdd ──────────────────────────────────────────────
    getSaleDeliveryOrderForm: builder.query<SaleDeliveryOrderForm, number>({
      query: (id) => `/sale-delivery-orders/${id}/form`,
    }),
    createSaleDeliveryOrder: builder.mutation<{ delivery_order_scs_id: number }, SaleDeliveryOrderWrite>({
      query: (body) => ({ url: '/sale-delivery-orders', method: 'POST', body }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),
    updateSaleDeliveryOrder: builder.mutation<{ delivery_order_scs_id: number }, { id: number; body: SaleDeliveryOrderWrite }>({
      query: ({ id, body }) => ({ url: `/sale-delivery-orders/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleDeliveryOrder'],
    }),
    suggestDoNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-delivery-orders/suggest/do-no',
    }),
    suggestIcoNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-delivery-orders/suggest/ico-no',
    }),
    checkDoNo: builder.query<SuggestIndexResponse, { index: number; excludeId?: number }>({
      query: ({ index, excludeId }) => ({
        url: '/sale-delivery-orders/check/do-no',
        params: { index, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getDoSnapshot: builder.query<DoSnapshotResponse, { saleContract: string; excludeId?: number }>({
      query: ({ saleContract, excludeId }) => ({
        url: '/sale-delivery-orders/snapshot',
        params: { saleContract, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups (combo sources)
    doLookupSaleContracts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/sale-contracts', params: q ? { q } : {} }),
    }),
    doLookupWarehouses: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/warehouses', params: q ? { q } : {} }),
    }),
    doLookupDunnage: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/dunnage', params: q ? { q } : {} }),
    }),
    doLookupInspectionTypes: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/inspection-types', params: q ? { q } : {} }),
    }),
    doLookupServices: builder.query<DoLookupResponse, { types: string; q?: string }>({
      query: ({ types, q }) => ({ url: '/sale-delivery-orders/lookups/services', params: { types, ...(q ? { q } : {}) } }),
    }),
    doLookupDocsDispatch: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/docs-dispatch', params: q ? { q } : {} }),
    }),
    doLookupPorts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/ports', params: q ? { q } : {} }),
    }),
    doLookupVessels: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/vessels', params: q ? { q } : {} }),
    }),
    doLookupExportTerminals: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/export-terminals', params: q ? { q } : {} }),
    }),
    doLookupInternalPorts: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/internal-ports', params: q ? { q } : {} }),
    }),
    doLookupPics: builder.query<DoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-delivery-orders/lookups/pics', params: q ? { q } : {} }),
    }),
    // Print — SA (BL No): Access rpCtrlSalesSA master-detail report → web HTML.
    // BE renders one Shipping Advice grouped by sale_contract + b_l_number.
    // 422 detail = user-facing English message (no rows / missing keys).
    getSaPrintPreview: builder.query<
      { title: string; html: string; message?: string },
      { saleContract: string; blNumber: string }
    >({
      query: ({ saleContract, blNumber }) => ({
        url: '/sale-delivery-orders/sa-print/preview',
        params: { saleContract, blNumber },
      }),
      keepUnusedDataFor: 0,
    }),
    // Print — DO: render rpCtrlSalesDO for one delivery order (do_no).
    getDoPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/do-print/preview', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // Print — Dispatch: render rpCtrlSalesDispatch cover letter for one do_no.
    getDispatchPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/dispatch-print/preview', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // Print — Dispatch (editable): frmCtrlSalesDispatchPrint subform — load + persist
    // the shipping-document list, then render. btnPrint = save → render.
    getDispatchPrintForm: builder.query<DispatchPrintForm, string>({
      query: (doNo) => ({ url: '/sale-delivery-orders/dispatch-print/form', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    saveDispatchPrint: builder.mutation<
      { title: string; html: string; message?: string },
      { doNo: string; rows: DispatchDocRow[] }
    >({
      query: (body) => ({ url: '/sale-delivery-orders/dispatch-print/save', method: 'POST', body }),
    }),
  }),
})

export const {
  useGetSaleDeliveryOrdersQuery,
  useGetSaleDeliveryOrderQuery,
  useDeleteSaleDeliveryOrderMutation,
  useGetSaleDeliveryOrderCanHardDeleteQuery,
  useLazyGetSaleDeliveryOrderFormQuery,
  useCreateSaleDeliveryOrderMutation,
  useUpdateSaleDeliveryOrderMutation,
  useLazySuggestDoNoQuery,
  useLazySuggestIcoNoQuery,
  useLazyCheckDoNoQuery,
  useLazyGetDoSnapshotQuery,
  useDoLookupSaleContractsQuery,
  useDoLookupWarehousesQuery,
  useDoLookupDunnageQuery,
  useDoLookupInspectionTypesQuery,
  useDoLookupServicesQuery,
  useDoLookupDocsDispatchQuery,
  useDoLookupPortsQuery,
  useDoLookupVesselsQuery,
  useDoLookupExportTerminalsQuery,
  useDoLookupInternalPortsQuery,
  useDoLookupPicsQuery,
  useLazyGetSaPrintPreviewQuery,
  useLazyGetDoPrintPreviewQuery,
  useLazyGetDispatchPrintPreviewQuery,
  useLazyGetDispatchPrintFormQuery,
  useSaveDispatchPrintMutation,
} = saleDeliveryOrderApi

export default saleDeliveryOrderApi
